import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import RestaurantCandidates from '../components/RestaurantCandidates';

const TABS = ['pending', 'approved', 'rejected'];

export default function AdminQueue() {
  const { user, isAuthenticated, loading } = useAuth();
  const [mode, setMode] = useState('candidates');
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!isAuthenticated) return;
    setBusy(true); setErr('');
    apiFetch(`/api/admin/submissions?status=${tab}`)
      .then((list) => setItems(list || []))
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(false));
  }, [tab, isAuthenticated]);

  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (err && err.includes('Admin')) return <div className="min-h-screen flex items-center justify-center text-gray-700">Admin access required.</div>;

  const act = async (id, action) => {
    try {
      await apiFetch(`/api/admin/submissions/${id}/${action}`, { method: 'POST' });
      setItems((s) => s.filter((x) => x.id !== id));
    } catch (e) { setErr(e.message); }
  };

  const filtered = items.filter((i) => {
    const s = q.toLowerCase();
    return !s || [i.name, i.city, i.cuisine, i.contact_email].some((v) => (v || '').toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Culinera Admin</span>
          </Link>
          <Link to="/" className="text-teal-600 hover:text-teal-700 font-semibold">← Site</Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Admin Console</h1>
        <p className="text-sm text-gray-500 mb-6">Logged in as <strong>{user?.email}</strong> · <a href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/export.csv`} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline font-semibold">📊 Export community CSV</a></p>

        <div className="flex gap-2 mb-6">
          <button
            data-testid="admin-mode-candidates"
            onClick={() => setMode('candidates')}
            className={`px-4 py-2 text-sm font-bold rounded-lg ${mode === 'candidates' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 shadow-sm hover:text-gray-900'}`}
          >🍽 Restaurant Candidates</button>
          <button
            data-testid="admin-mode-trucks"
            onClick={() => setMode('trucks')}
            className={`px-4 py-2 text-sm font-bold rounded-lg ${mode === 'trucks' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 shadow-sm hover:text-gray-900'}`}
          >🚚 Food Truck Submissions</button>
        </div>

        {mode === 'candidates' && <RestaurantCandidates />}

        {mode === 'trucks' && (
        <div>
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex bg-white rounded-lg shadow-sm p-1">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`admin-tab-${t}`}
                onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-semibold rounded-md ${tab === t ? 'bg-teal-600 text-white' : 'text-gray-600 hover:text-teal-600'}`}
              >{t.charAt(0).toUpperCase() + t.slice(1)}</button>
            ))}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, city, email…" className="flex-1 p-2 border border-gray-300 rounded-lg text-sm" />
        </div>

        {err && !err.includes('Admin') && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-3 text-sm">{err}</div>}
        {busy && <div className="text-gray-500 text-sm">Loading…</div>}
        {!busy && filtered.length === 0 && <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-500">No {tab} submissions.</div>}

        <ul className="space-y-3">
          {filtered.map((s) => (
            <li key={s.id} data-testid={`admin-row-${s.id}`} className="bg-white rounded-lg shadow-sm p-4 flex items-start justify-between gap-3 text-sm">
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{s.name} <span className="text-gray-500 font-normal">· {s.city} · {s.cuisine} · {s.price}</span></div>
                <div className="text-gray-700 mt-1">{s.description}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {s.instagram && <>IG: @{s.instagram} · </>}
                  {s.twitter && <>X: @{s.twitter} · </>}
                  {s.website && <>{s.website} · </>}
                  Submitted by {s.contact_email} on {new Date(s.submitted_at).toLocaleDateString()}
                </div>
              </div>
              {tab === 'pending' && (
                <div className="flex flex-col gap-1 shrink-0">
                  <button data-testid={`admin-approve-${s.id}`} onClick={() => act(s.id, 'approve')} className="bg-teal-600 text-white text-xs px-3 py-1 rounded hover:bg-teal-700">Approve</button>
                  <button data-testid={`admin-reject-${s.id}`} onClick={() => act(s.id, 'reject')} className="border border-gray-300 text-gray-700 text-xs px-3 py-1 rounded hover:bg-gray-50">Reject</button>
                </div>
              )}
              {tab === 'approved' && (
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    data-testid={`admin-feature-${s.id}`}
                    onClick={async () => {
                      try {
                        const r = await apiFetch(`/api/admin/submissions/${s.id}/feature`, { method: 'POST' });
                        setItems((list) => list.map((x) => x.id === s.id ? { ...x, is_featured: r.is_featured } : x));
                      } catch (e) { setErr(e.message); }
                    }}
                    className={`text-xs px-3 py-1 rounded font-semibold ${s.is_featured ? 'bg-yellow-400 text-yellow-900 hover:bg-yellow-500' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                  >{s.is_featured ? '⭐ Featured' : '☆ Feature'}</button>
                </div>
              )}
            </li>
          ))}
        </ul>
        </div>
        )}
      </main>
    </div>
  );
}
